/**
 * KAN-810: lead temperature in the client card. A choice is saved at once
 * (PATCH /clients/{id}/ lead_temperature, "Не задано" -> ""): a manual value
 * is never overwritten by the bot. Under the select: who set the level and why.
 */
import { useEffect, useState } from 'react';
import { NativeSelect } from '@/components/ui/native-select';
import { useSetClientLeadTemperature } from '@/hooks/useLeadTemperature';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
  LEAD_TEMPERATURE_LABELS,
  LEAD_TEMPERATURE_LEVELS,
  LEAD_TEMPERATURE_SOURCE_LABELS,
  isLeadTemperatureLevel,
  isLeadTemperatureSource,
} from '@/lib/leadTemperature';

interface ClientLeadTemperatureSelectProps {
  clientId: string;
  level: string | null | undefined;
  source: string | null | undefined;
  reason: string | null | undefined;
}

const OPTIONS = [
  { value: '', label: 'Не задано' },
  ...LEAD_TEMPERATURE_LEVELS.map((level) => ({ value: level, label: LEAD_TEMPERATURE_LABELS[level] })),
];

export function ClientLeadTemperatureSelect({ clientId, level, source, reason }: ClientLeadTemperatureSelectProps) {
  const current = isLeadTemperatureLevel(level) ? level : '';
  const [value, setValue] = useState<string>(current);
  const save = useSetClientLeadTemperature(clientId);

  useEffect(() => setValue(current), [current]);

  const onChange = (next: string) => {
    setValue(next);
    save.mutate(next, { onError: () => setValue(current) });
  };

  return (
    <div className="space-y-1" data-testid="client-lead-temperature">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={`lead-temperature-${clientId}`} className="shrink-0 text-sm text-gray-500">
          Теплота
        </label>
        <div className="w-56 max-w-full">
          <NativeSelect
            id={`lead-temperature-${clientId}`}
            data-testid="client-lead-temperature-select"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={save.isPending}
            options={OPTIONS}
            className="h-9"
          />
        </div>
      </div>
      {current && isLeadTemperatureSource(source) && (
        <p className="text-right text-xs text-gray-500">
          Источник:{' '}
          <span data-testid="client-lead-temperature-source">{LEAD_TEMPERATURE_SOURCE_LABELS[source]}</span>
        </p>
      )}
      {current && reason && (
        <p data-testid="client-lead-temperature-reason" className="text-right text-xs text-gray-600">
          {reason}
        </p>
      )}
      {save.isError && (
        <p data-testid="client-lead-temperature-error" className="text-right text-xs text-red-700">
          {extractErrorMessage(save.error, 'Не удалось сохранить теплоту')}
        </p>
      )}
    </div>
  );
}
